import { apiFetch } from '@/api/client'
import type { SortDirection } from '@/api/sorting'

/**
 * How a contact is addressed. Language-independent on purpose: a contact carries its own
 * `language`, and an English follow-up to Frau Meyer opens with "Dear Ms. Meyer". The wording —
 * "Herr", "Frau", "–" — belongs to whatever renders it.
 */
export const Salutation = {
  MR: 'MR',
  MS: 'MS',
  /** The "–" in the picker: a contact addressed without one. */
  NONE: 'NONE',
} as const

export type Salutation = (typeof Salutation)[keyof typeof Salutation]

/** The language a contact is written to in — not the language of the UI. */
export type Address = {
  street: string | null
  postalCode: string | null
  city: string | null
  country: string | null
}

export type Contact = {
  id: string
  salutation: Salutation
  /** Optional where the surname is not: a card often carries only "A. Meyer". */
  firstName: string | null
  lastName: string
  position: string | null
  email: string | null
  phone: string | null
  mobile: string | null
  primaryContact: boolean
}

export type Customer = {
  id: string
  companyName: string
  /** From the ERP, where it identifies exactly one company. Unique across live records. */
  customerNumber: string | null
  address: Address
  phone: string | null
  email: string | null
  website: string | null
  notes: string | null
  /**
   * Soft-deleted: out of the default listing, still there. Leads point back at customers, and
   * removing the row would lose the trail from a lead to the company it belongs to.
   */
  deleted: boolean
  contacts: Contact[]
}

export type CustomerPage = {
  customers: Customer[]
  page: number
  size: number
  total: number
}

/** The columns the customer table has. The backend refuses anything else with a 400. */
export const CustomerSort = {
  COMPANY_NAME: 'COMPANY_NAME',
  CUSTOMER_NUMBER: 'CUSTOMER_NUMBER',
  CITY: 'CITY',
} as const

export type CustomerSort = (typeof CustomerSort)[keyof typeof CustomerSort]

export type CustomerListQuery = {
  search?: string
  includeDeleted?: boolean
  sort?: CustomerSort | null
  direction?: SortDirection | null
  page?: number
  size?: number
}

export const DEFAULT_PAGE_SIZE = 20

/**
 * Searches company name, customer number, city **and the contacts' names** — the company somebody
 * remembers meeting is often remembered by the person they met.
 */
export function listCustomers(
  query: CustomerListQuery = {},
  signal?: AbortSignal,
): Promise<CustomerPage> {
  const parameters = new URLSearchParams()
  if (query.search) {
    parameters.set('search', query.search)
  }
  if (query.includeDeleted) {
    parameters.set('includeDeleted', 'true')
  }
  if (query.sort) {
    parameters.set('sort', query.sort)
  }
  if (query.sort && query.direction) {
    parameters.set('direction', query.direction)
  }
  parameters.set('page', String(query.page ?? 0))
  parameters.set('size', String(query.size ?? DEFAULT_PAGE_SIZE))
  return apiFetch<CustomerPage>(`/api/customers?${parameters}`, { signal })
}

export type CustomerMatches = {
  /** At most five, and empty when there was nothing worth matching on. */
  customers: Customer[]
}

/**
 * Master records that might be the company being typed at a stand.
 *
 * Deliberately not `listCustomers`: that one casts a wide net — the city and the contacts' names
 * included — and a hint saying "this company already exists" that fired because somebody typed a
 * person's name would be worse than no hint. This matches on identity only: the customer number
 * exactly, the company name either way round.
 */
export function findCustomerMatches(
  companyName: string,
  customerNumber: string | null,
  signal?: AbortSignal,
): Promise<CustomerMatches> {
  const parameters = new URLSearchParams()
  if (companyName) {
    parameters.set('companyName', companyName)
  }
  if (customerNumber) {
    parameters.set('customerNumber', customerNumber)
  }
  return apiFetch<CustomerMatches>(`/api/customers/matches?${parameters}`, { signal })
}

export function getCustomer(id: string, signal?: AbortSignal): Promise<Customer> {
  return apiFetch<Customer>(`/api/customers/${encodeURIComponent(id)}`, { signal })
}

/** Administrators only, and a soft delete. `restoreCustomer` is the way back. */
export function deleteCustomer(id: string): Promise<Customer> {
  return apiFetch<Customer>(`/api/customers/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function restoreCustomer(id: string): Promise<Customer> {
  return apiFetch<Customer>(`/api/customers/${encodeURIComponent(id)}/restore`, { method: 'POST' })
}

/** "Meyer, Anna" — the form the capture screen puts in its header. */
export function contactName(contact: Pick<Contact, 'firstName' | 'lastName'>): string {
  return contact.firstName ? `${contact.lastName}, ${contact.firstName}` : contact.lastName
}

/** The one to write to, or the first there is. */
export function primaryContact(customer: Customer): Contact | undefined {
  return customer.contacts.find((contact) => contact.primaryContact) ?? customer.contacts[0]
}
